import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Upload, Download, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROW_STYLE = {
  ok: "text-emerald-300",
  duplicate: "text-slate-500",
  error: "text-red-300",
};

/** Import a league's fixture list from the CSV/Excel they publish. */
export const FixtureImport = ({ teamId, teamName, onImported }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(true);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/academy/fixtures/import-template`, {
        responseType: "blob", withCredentials: true,
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "league-fixtures-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the template");
    }
  };

  const send = async (dryRun) => {
    if (!teamId) { toast.error("Pick the league team first"); return; }
    if (!file) { toast.error("Choose the league's CSV or Excel file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dryRun ? "true" : "false");
      fd.append("notify", notify ? "true" : "false");
      const r = await axios.post(`${API}/academy/teams/${teamId}/fixtures/import`, fd, {
        withCredentials: true,
      });
      setPreview(r.data);
      if (!dryRun) {
        toast.success(`${r.data.imported} game(s) added · ${r.data.parents_notified} parent(s) told`);
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
        onImported?.();
      } else {
        toast.success(`${r.data.counts.ready} of ${r.data.counts.total} rows ready`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read that file");
    } finally {
      setBusy(false);
    }
  };

  const counts = preview?.counts;

  return (
    <Card className="bg-slate-900/60 border-slate-800" data-testid="fixture-import-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Upload className="w-4 h-4 text-sky-400" /> Import the league's fixture list
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-400">
          Save the league's PDF as CSV or Excel, then drop it here. Columns: Date, Kick off,
          Opponent, Venue, Home/Away, Team, Notes. Dates read UK-style (12/07/2026 is 12 July).
          Add a <span className="text-slate-300">Team</span> column to load JSSL and SYL from one
          file. Parents get one summary per team, not a message per game.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={downloadTemplate}
            className="h-10 border-slate-700 text-slate-200" data-testid="fixture-template-btn">
            <Download className="w-3 h-3 mr-1" /> Download template
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls,.xlsm,text/csv"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            className="text-xs text-slate-300 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-700 file:text-white"
            data-testid="fixture-import-file"
          />
        </div>

        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
            className="w-4 h-4" data-testid="fixture-import-notify" />
          Tell parents once the games are in
        </label>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => send(true)} disabled={busy || !file}
            className="h-10 bg-slate-700 hover:bg-slate-600" data-testid="fixture-import-preview-btn">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Check the file
          </Button>
          <Button size="sm" onClick={() => send(false)} disabled={busy || !counts?.ready}
            className="h-10 bg-emerald-600 hover:bg-emerald-500" data-testid="fixture-import-commit-btn">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Add {counts?.ready || 0} game{counts?.ready === 1 ? "" : "s"}
            {teamName ? ` to ${teamName}` : ""}
          </Button>
        </div>

        {preview && (
          <div className="rounded-lg border border-slate-800 bg-slate-950/70 p-3"
            data-testid="fixture-import-preview">
            <div className="flex flex-wrap gap-2 mb-2 text-[11px]">
              <Badge className="bg-emerald-500/20 text-emerald-300">{counts.ready} ready</Badge>
              <Badge className="bg-slate-600/30 text-slate-300">{counts.duplicates} already in</Badge>
              <Badge className="bg-red-500/20 text-red-300">{counts.errors} need a look</Badge>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {preview.rows.map((r) => (
                <div key={r.row} className={`text-[11px] ${ROW_STYLE[r.status]}`}
                  data-testid={`import-row-${r.row}`}>
                  <span className="text-slate-500 mr-2">row {r.row}</span>
                  {r.date || "?"} {r.start_time || ""} · {r.home_away} vs {r.opponent || "?"}
                  {r.venue ? ` · ${r.venue}` : ""}
                  <span className="text-slate-500"> · {r.team_name}</span>
                  {r.error ? <span className="ml-1">— {r.error}</span> : ""}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
